import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, LogOut, Wallet, Users, Pause, Play, X, KeyRound, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ugx, shortDate } from "@/lib/format";
import { StatusPill } from "@/components/Shell";

const PLANS = ["starter", "business", "enterprise"];
const inp = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500";
const addMonths = (n) => { const d = new Date(); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 10); };

const Field = ({ label, children }) => (
  <div><label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>{children}</div>
);

export default function Platform() {
  const { user, logout } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const blank = { name: "", tin: "", address: "", phone: "", email: "", admin_name: "", admin_email: "", admin_password: "",
    plan: "starter", amount_paid: "", paid_until: addMonths(12), payment_method: "bank", payment_reference: "" };
  const [form, setForm] = useState(blank);
  const [pay, setPay] = useState({ amount: "", plan: "starter", paid_until: addMonths(12), method: "bank", reference: "" });
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "accountant" });

  const load = () => {
    api.get("/platform/organizations").then(({ data }) => setOrgs(data));
    api.get("/platform/summary").then(({ data }) => setSummary(data));
  };
  useEffect(load, []);

  const createOrg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/organizations", { ...form, amount_paid: Number(form.amount_paid || 0), paid_until: form.paid_until || null });
      toast.success(`${form.name} registered`);
      setOpen(false); setForm(blank); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/platform/organizations/${payFor.id}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded — organisation active");
      setPayFor(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const setStatus = async (o, status) => {
    try { await api.patch(`/platform/organizations/${o.id}`, { status }); toast.success(`${o.name} ${status}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const removeOrg = async (o) => {
    if (!window.confirm(`Permanently delete ${o.name} and ALL its data (users, invoices, books)? This cannot be undone.`)) return;
    try { await api.delete(`/platform/organizations/${o.id}`); toast.success(`${o.name} deleted`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openDetail = (o) => api.get(`/platform/organizations/${o.id}`).then(({ data }) => setDetail(data));

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/platform/organizations/${detail.id}/users`, newUser);
      toast.success("User added"); setNewUser({ name: "", email: "", password: "", role: "accountant" }); openDetail(detail); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const resetPw = async (m) => {
    const password = window.prompt(`New password for ${m.email} (min 8 chars)`);
    if (!password) return;
    try { await api.post(`/platform/organizations/${detail.id}/users/${m.id}/reset-password`, { password }); toast.success("Password reset"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="platform-page">
      <header className="flex h-16 items-center justify-between border-b border-white/10 bg-[#0F172A] px-6 text-white">
        <div className="flex items-center gap-3">
          <span className="grid h-8 w-8 place-items-center rounded bg-emerald-500 font-display text-sm font-extrabold text-[#0F172A]">NB</span>
          <span className="font-display text-lg font-bold">NeroBooks</span>
          <span className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-300">Super user</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-300" data-testid="platform-user-email">{user.email}</span>
          <button data-testid="platform-logout-button" onClick={logout} className="flex items-center gap-1 text-slate-300 hover:text-white"><LogOut size={15} /> Sign out</button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Organisations</h1>
            <p className="text-sm text-slate-500">Register a business once payment is confirmed, record renewals, suspend or reactivate access.</p>
          </div>
          <button data-testid="add-organization-button" onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus size={16} /> Register organisation
          </button>
        </div>

        {summary && (
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="platform-summary">
            {[["Organisations", summary.organizations], ["Active", summary.active], ["Pending payment", summary.pending],
              ["Expired", summary.expired], ["Total collected", ugx(summary.revenue)]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500">{l}</p>
                <p className="mt-1 font-display text-xl font-bold text-slate-900">{v}</p>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {orgs.length ? (
            <table className="w-full text-sm" data-testid="organizations-table">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2.5">Organisation</th><th className="px-5 py-2.5">TIN</th><th className="px-5 py-2.5">Plan</th>
                  <th className="px-5 py-2.5">Paid until</th><th className="px-5 py-2.5">Users</th><th className="px-5 py-2.5">Status</th><th className="px-5 py-2.5 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid="organization-row">
                    <td className="px-5 py-3">
                      <button data-testid="open-organization-button" onClick={() => openDetail(o)} className="flex items-center gap-2 font-semibold text-slate-900 hover:text-emerald-700">
                        <Building2 size={15} className="text-slate-400" /> {o.name}
                      </button>
                      <p className="text-xs text-slate-500">{o.email || o.phone || "—"}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{o.tin || "—"}</td>
                    <td className="px-5 py-3 capitalize text-slate-600">{o.plan}</td>
                    <td className="px-5 py-3 text-slate-600">{o.paid_until ? shortDate(o.paid_until) : "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{o.users}</td>
                    <td className="px-5 py-3"><StatusPill status={o.is_active ? "active" : o.status === "suspended" ? "suspended" : o.status === "pending" ? "pending" : "expired"} /></td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button data-testid="record-payment-button" onClick={() => { setPayFor(o); setPay({ ...pay, plan: o.plan }); }}
                          className="flex items-center gap-1 rounded bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-emerald-700"><Wallet size={12} /> Record payment</button>
                        {o.status === "suspended"
                          ? <>
                              <button data-testid="activate-organization-button" onClick={() => setStatus(o, "active")} className="text-slate-400 hover:text-emerald-600" aria-label="Reactivate"><Play size={15} /></button>
                              <button data-testid="delete-organization-button" onClick={() => removeOrg(o)} className="text-slate-400 hover:text-rose-600" aria-label="Delete organisation"><Trash2 size={15} /></button>
                            </>
                          : <button data-testid="suspend-organization-button" onClick={() => setStatus(o, "suspended")} className="text-slate-400 hover:text-amber-600" aria-label="Suspend"><Pause size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="p-10 text-center text-sm text-slate-500" data-testid="organizations-empty">No organisations yet. Register the first business once its payment is confirmed.</p>}
        </div>
      </main>

      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 p-4">
          <form onSubmit={createOrg} className="mx-auto w-full max-w-2xl rounded-lg border border-slate-200 bg-white p-5 shadow-md" data-testid="organization-dialog">
            <div className="flex items-center justify-between"><h3 className="font-display text-base font-bold text-slate-900">Register organisation</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900" aria-label="Close"><X size={18} /></button></div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Business</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <Field label="Organisation name *"><input data-testid="org-name-input" required className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="TIN"><input data-testid="org-tin-input" className={inp} value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} /></Field>
              <Field label="Address"><input data-testid="org-address-input" className={inp} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
              <Field label="Phone"><input data-testid="org-phone-input" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <Field label="Business email"><input data-testid="org-email-input" type="email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Plan"><select data-testid="org-plan-select" className={inp} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-500">First administrator</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <Field label="Name *"><input data-testid="org-admin-name-input" required className={inp} value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} /></Field>
              <Field label="Email *"><input data-testid="org-admin-email-input" type="email" required className={inp} value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} /></Field>
              <Field label="Password * (min 8)"><input data-testid="org-admin-password-input" type="text" required minLength={8} className={inp} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /></Field>
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Payment (leave amount empty to register as pending)</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-4">
              <Field label="Amount paid (UGX)"><input data-testid="org-amount-input" type="number" min={0} className={inp} value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} /></Field>
              <Field label="Paid until"><input data-testid="org-paid-until-input" type="date" className={inp} value={form.paid_until} onChange={(e) => setForm({ ...form, paid_until: e.target.value })} /></Field>
              <Field label="Method"><select data-testid="org-payment-method-select" className={inp} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}><option value="bank">Bank transfer</option><option value="mobile_money">Mobile money</option><option value="cash">Cash</option></select></Field>
              <Field label="Reference"><input data-testid="org-payment-reference-input" className={inp} value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} /></Field>
            </div>
            <div className="mt-5 flex gap-2">
              <button data-testid="save-organization-button" className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Register organisation</button>
              <button type="button" onClick={() => setOpen(false)} className="flex-1 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {payFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <form onSubmit={recordPayment} className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-md" data-testid="payment-dialog">
            <h3 className="font-display text-base font-bold text-slate-900">Record payment · {payFor.name}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Amount (UGX) *"><input data-testid="payment-amount-input" type="number" min={1} required className={inp} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></Field>
              <Field label="Plan"><select data-testid="payment-plan-select" className={inp} value={pay.plan} onChange={(e) => setPay({ ...pay, plan: e.target.value })}>{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
              <Field label="Paid until *"><input data-testid="payment-paid-until-input" type="date" required className={inp} value={pay.paid_until} onChange={(e) => setPay({ ...pay, paid_until: e.target.value })} /></Field>
              <Field label="Method"><select data-testid="payment-method-select" className={inp} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}><option value="bank">Bank transfer</option><option value="mobile_money">Mobile money</option><option value="cash">Cash</option></select></Field>
              <div className="sm:col-span-2"><Field label="Reference"><input data-testid="payment-reference-input" className={inp} value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></Field></div>
            </div>
            <div className="mt-5 flex gap-2">
              <button data-testid="save-payment-button" className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Record & activate</button>
              <button type="button" onClick={() => setPayFor(null)} className="flex-1 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 p-4">
          <div className="mx-auto w-full max-w-3xl rounded-lg border border-slate-200 bg-white p-5 shadow-md" data-testid="organization-detail-dialog">
            <div className="flex items-start justify-between">
              <div><h3 className="font-display text-base font-bold text-slate-900">{detail.name}</h3>
                <p className="text-xs text-slate-500">{[detail.tin && `TIN ${detail.tin}`, detail.address, detail.phone].filter(Boolean).join(" · ")}</p></div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-900" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="mt-5 grid gap-6 lg:grid-cols-2">
              <div>
                <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500"><Users size={13} /> Users</p>
                <ul className="divide-y divide-slate-100 rounded border border-slate-200 text-sm" data-testid="organization-members">
                  {detail.members.map((m) => (
                    <li key={m.id} className="flex items-center justify-between px-3 py-2">
                      <div><p className="font-medium text-slate-900">{m.name}</p><p className="text-xs text-slate-500">{m.email} · <span className="capitalize">{m.role}</span></p></div>
                      <button data-testid="reset-member-password-button" onClick={() => resetPw(m)} className="text-slate-400 hover:text-slate-900" aria-label="Reset password"><KeyRound size={14} /></button>
                    </li>
                  ))}
                </ul>
                <form onSubmit={addUser} className="mt-3 grid gap-2 sm:grid-cols-2">
                  <input data-testid="member-name-input" required placeholder="Name" className={inp} value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
                  <input data-testid="member-email-input" required type="email" placeholder="Email" className={inp} value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                  <input data-testid="member-password-input" required minLength={8} placeholder="Password" className={inp} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
                  <div className="flex gap-2">
                    <select data-testid="member-role-select" className={inp} value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}><option value="admin">Admin</option><option value="accountant">Accountant</option><option value="viewer">Viewer</option></select>
                    <button data-testid="add-member-button" className="rounded-md bg-slate-900 px-3 text-sm font-semibold text-white">Add</button>
                  </div>
                </form>
              </div>
              <div>
                <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500"><Wallet size={13} /> Payments</p>
                {detail.payments.length ? (
                  <ul className="divide-y divide-slate-100 rounded border border-slate-200 text-sm" data-testid="organization-payments">
                    {detail.payments.map((p) => (
                      <li key={p.id} className="flex items-center justify-between px-3 py-2">
                        <div><p className="font-medium text-slate-900">{ugx(p.amount)} · <span className="capitalize">{p.plan}</span></p>
                          <p className="text-xs text-slate-500">{shortDate(p.created_at)} · until {shortDate(p.paid_until)} · {p.method}{p.reference ? ` · ${p.reference}` : ""}</p></div>
                      </li>
                    ))}
                  </ul>
                ) : <p className="rounded border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">No payments recorded.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
